import { useNavigate } from 'react-router-dom';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Constants } from '../../../utils/constants';
import { formatNaira, formatCount } from '../../../utils/money';
import PageHeader from '../../shell/PageHeader';
import {
  DataTable,
  DateRangeFilter,
  FilterSelect,
  Pagination,
  SearchInput,
  StatusPill,
  TableCard,
} from '../../ui';
import { useServerTable } from '../../../hooks/useServerTable';

const IssuedUsers = () => {
  const navigate = useNavigate();

  const table = useServerTable(PartnerEndpoints.externalUsers, {
    sort: 'name',
    filters: { status: '' },
    fallbackError: 'We could not load your issued users. Please try again.',
  });

  // total_transactions is Naira volume, not a count of transactions. The endpoint cannot sort on
  // it, nor on pan, so neither column carries a sortKey.
  const columns = [
    { key: 'name', header: 'Payer Name', sortKey: 'name', render: (user) => user.name || '—' },
    { key: 'pan', header: 'PAN', render: (user) => user.pan || '—' },
    {
      key: 'total_transactions',
      header: 'Volume',
      align: 'right',
      render: (user) => formatNaira(user.total_transactions),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (user) => <StatusPill status={user.status} />,
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={Constants.externalUserStatuses}
          label="All"
          ariaLabel="Filter by status"
        />
      ),
    },
    { key: 'sub_merchant', header: 'Sub-merchant', render: (user) => user.sub_merchant?.name ?? '—' },
  ];

  return (
    <>
      <PageHeader
        title="Issued Users"
        breadcrumbs={[{ label: 'Home', to: '/layout/dashboard' }, { label: 'Issued Users' }]}
      />

      <TableCard
        title="Issued Users"
        summary={{ label: 'Showing', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <>
            <SearchInput
              value={table.search}
              onChange={table.setSearch}
              placeholder="Search name, PAN, email or phone"
            />

            {/* The row shape has no created_at to hang a column filter under, but the endpoint
                still filters on it, so the date range lives in the toolbar on this page. */}
            <DateRangeFilter
              label="Registered"
              from={table.from}
              to={table.to}
              onFromChange={table.setFrom}
              onToChange={table.setTo}
            />
          </>
        }
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(user) => user.pan}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{ title: 'No issued users', message: 'No users match the current filters.' }}
          onRowClick={(user) => navigate('/layout/issued-users/issued-user-log', { state: { user } })}
        />
      </TableCard>
    </>
  );
};

export default IssuedUsers;
